import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Badge,
  Chip,
  Tooltip,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  CameraAlt as CameraAltIcon,
  Star as StarIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';

const DrawerHeader = ({ userProfile, setCurrentTab, setProfileInfoOpen, onQRCodeClick }) => {
  return (
    <Box 
      sx={{ 
        p: 3, 
        borderBottom: '1px solid #e7e5e4',
        background: 'linear-gradient(135deg, #1c1917 0%, #292524 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.1
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              userProfile.photo ? (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    border: '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <StarIcon sx={{ fontSize: 8, color: 'white' }} />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: '#f59e0b',
                    border: '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 8, color: 'white' }} />
                </Box>
              )
            }
          >
            <Avatar
              src={userProfile.photo}
              sx={{
                width: 56,
                height: 56,
                bgcolor: userProfile.photo ? 'transparent' : 'rgba(255,255,255,0.2)',
                border: '3px solid rgba(255,255,255,0.2)',
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                  border: '3px solid rgba(255,255,255,0.4)'
                }
              }}
              onClick={() => setCurrentTab(0)}
            >
              {!userProfile.photo && (
                <PersonIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.8)' }} />
              )}
            </Avatar>
          </Badge>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: 'white', 
                mb: 0.5,
                fontSize: '1.1rem',
                lineHeight: 1.2,
                cursor: 'pointer',
                '&:hover': { opacity: 0.9 }
              }}
              onClick={() => setCurrentTab(0)}
            >
              {userProfile.salonName || 'Mon Salon'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '0.75rem'
                }}
              >
                Dashboard
              </Typography>
              {userProfile.subscription?.plan === 'trial' && (
                <Chip
                  label="Essai"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.65rem',
                    bgcolor: 'rgba(251,191,36,0.2)',
                    color: '#fbbf24',
                    border: '1px solid rgba(251,191,36,0.3)',
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                  onClick={() => {}}
                />
              )}
            </Stack>
          </Box>
        </Box>
        
        {/* Profile completion indicator */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
              Profil complété
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.7rem' }}>
                {Math.round(((userProfile.salonName ? 1 : 0) +
                           (userProfile.photo ? 1 : 0) +
                           (userProfile.photos?.length > 0 ? 1 : 0) +
                           (userProfile.description ? 1 : 0) +
                           (userProfile.address ? 1 : 0) +
                           (userProfile.googleMyBusinessUrl ? 1 : 0)) / 6 * 100)}%
              </Typography>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                      Pour compléter votre profil :
                    </Typography>
                    <Stack spacing={0.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userProfile.salonName ? (
                          <CheckIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        ) : (
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1px solid #94a3b8' }} />
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            textDecoration: userProfile.salonName ? 'line-through' : 'none',
                            opacity: userProfile.salonName ? 0.7 : 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Nom du salon
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userProfile.photo ? (
                          <CheckIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        ) : (
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1px solid #94a3b8' }} />
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            textDecoration: userProfile.photo ? 'line-through' : 'none',
                            opacity: userProfile.photo ? 0.7 : 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Photo de profil
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userProfile.photos?.length > 0 ? (
                          <CheckIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        ) : (
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1px solid #94a3b8' }} />
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            textDecoration: userProfile.photos?.length > 0 ? 'line-through' : 'none',
                            opacity: userProfile.photos?.length > 0 ? 0.7 : 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Photos du salon
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userProfile.description ? (
                          <CheckIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        ) : (
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1px solid #94a3b8' }} />
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            textDecoration: userProfile.description ? 'line-through' : 'none',
                            opacity: userProfile.description ? 0.7 : 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Description
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userProfile.address ? (
                          <CheckIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        ) : (
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1px solid #94a3b8' }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: userProfile.address ? 'line-through' : 'none',
                            opacity: userProfile.address ? 0.7 : 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Adresse
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userProfile.googleMyBusinessUrl ? (
                          <CheckIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        ) : (
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1px solid #94a3b8' }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: userProfile.googleMyBusinessUrl ? 'line-through' : 'none',
                            opacity: userProfile.googleMyBusinessUrl ? 0.7 : 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Google My Business
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                }
                arrow
                placement="top"
                sx={{
                  '& .MuiTooltip-tooltip': {
                    bgcolor: 'rgba(0,0,0,0.9)',
                    maxWidth: 200
                  }
                }}
              >
              <InfoIcon 
                onClick={() => setProfileInfoOpen(true)}
                sx={{ 
                  fontSize: 12, 
                  color: 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'rgba(255,255,255,0.8)'
                  }
                }} 
              />
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              height: 4,
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                height: '100%',
                bgcolor: '#22c55e',
                borderRadius: 2,
                width: `${((userProfile.salonName ? 1 : 0) +
                         (userProfile.photo ? 1 : 0) +
                         (userProfile.photos?.length > 0 ? 1 : 0) +
                         (userProfile.description ? 1 : 0) +
                         (userProfile.address ? 1 : 0) +
                         (userProfile.googleMyBusinessUrl ? 1 : 0)) / 6 * 100}%`,
                transition: 'width 0.5s ease-in-out'
              }}
            />
          </Box>
        </Box>
        
        {/* QR Code Button */}
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<QrCodeIcon />}
            onClick={onQRCodeClick}
            sx={{
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'white',
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 0.8,
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Mon QR Code
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DrawerHeader;
